import _ from 'lodash';
import moment from 'moment';
import { escapeHTML } from './text';
import { PERCENTILE_LINE_COLORS, PERCENTILE_LABEL_COLORS } from './colors';

function underOnePercent(share) {
  return share > 0 && share < 1;
}

function shareLabel(percentage, whole = Math.round(percentage)) {
  return underOnePercent(percentage) ? '<1%' : `${whole}%`;
}

function wholeShares(values) {
  const total = _.sum(values);
  const shares = values.map(value => (total ? (value / total) * 100 : 0));
  const wholes = shares.map(share => (underOnePercent(share) ? 1 : Math.floor(share)));
  const missing = total ? Math.max(100 - _.sum(wholes), 0) : 0;
  const order = _.orderBy(
    _.range(values.length),
    [index => shares[index] - wholes[index], index => values[index]],
    ['desc', 'desc']
  );
  const raised = new Set(order.slice(0, missing));
  return shares.map((share, index) => shareLabel(share, wholes[index] + (raised.has(index) ? 1 : 0)));
}

const SEGMENT_LABEL_SAMPLE = '88%';
const SEGMENT_TEXT_COLORS = ['#FFFFFF', '#001A33'];

function textBox(chart, sample, fontSize, fontWeight) {
  const text = chart.renderer.text(sample, -9999, -9999).css({ fontSize, fontWeight }).add();
  const box = text.getBBox();
  text.destroy();
  return box;
}

function labelFits(chart, sample, fontSize, fontWeight, columnWidth) {
  if (!chart || !chart.renderer) {
    return false;
  }
  return columnWidth >= textBox(chart, sample, fontSize, fontWeight).width;
}

function parseColor(color) {
  const hex = /^#([0-9a-f]{6})$/i.exec(color || '');
  if (hex) {
    return [0, 2, 4].map(i => parseInt(hex[1].slice(i, i + 2), 16)).concat(1);
  }
  const rgb = /^rgba?\(([^)]+)\)$/.exec(color || '');
  if (rgb) {
    const [r, g, b, alpha = 1] = rgb[1].split(',').map(Number);
    return [r, g, b, alpha];
  }
  return null;
}

function withOpacity(color, opacity) {
  const [r, g, b] = parseColor(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

// WCAG 2's relative luminance and contrast ratio
function luminance(rgb) {
  const [r, g, b] = rgb.map(value => {
    const channel = value / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(first, second) {
  const [light, dark] = [luminance(first), luminance(second)].sort((a, b) => b - a);
  return (light + 0.05) / (dark + 0.05);
}

// Highcharts' 'contrast' text colour ignores a segment's transparency, so on the see-through colours drawn over the
// white card it chose white text at 1.4:1 to 2.8:1; the colour as it shows decides here
function shownColor(color) {
  const parsed = parseColor(color);
  if (!parsed) {
    return null;
  }
  const [r, g, b, alpha] = parsed;
  return [r, g, b].map(channel => alpha * channel + (1 - alpha) * 255);
}

function segmentTextColor(color) {
  const shown = shownColor(color);
  return shown && _.maxBy(SEGMENT_TEXT_COLORS, text => contrastRatio(parseColor(text).slice(0, 3), shown));
}

const BRAND_BLACK = '#071426';
const STATE_FILL_SHARE = 0.6;
const STATE_EDGE_CONTRAST = 3;
const WHITE_RGB = [255, 255, 255];
const PIE_NAMES_LEFT = 15;

function toHex(rgb) {
  return `#${rgb.map(channel => Math.round(channel).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}

function stateFill(color) {
  const shown = shownColor(color);
  return shown ? toHex(shown.map(channel => channel * STATE_FILL_SHARE + 255 * (1 - STATE_FILL_SHARE))) : color;
}

function stateEdge(color, colors = [color]) {
  const shown = shownColor(color);
  if (!shown) {
    return color;
  }
  const black = parseColor(BRAND_BLACK).slice(0, 3);
  const fills = _.compact(colors.map(other => parseColor(stateFill(other)))).map(fill => fill.slice(0, 3));
  const reads = mix => contrastRatio(mix, WHITE_RGB) >= STATE_EDGE_CONTRAST && fills.every(fill => contrastRatio(mix, fill) >= STATE_EDGE_CONTRAST);
  const steps = _.range(0, 101).map(step => shown.map((channel, i) => Math.round(channel + ((black[i] - channel) * step) / 100)));
  return toHex(_.find(steps, reads) || black);
}

function paleMarkEdge(color) {
  const shown = shownColor(color);
  return shown && contrastRatio(shown, WHITE_RGB) < STATE_EDGE_CONTRAST ? stateEdge(color, []) : null;
}

function stateSeries(items, edgeKey) {
  const colors = _.map(items, 'color');
  return _.map(items, item => ({ ...item, color: stateFill(item.color), [edgeKey]: stateEdge(item.color, colors) }));
}

function drawStateEdges(chart, edgeColor) {
  if (chart.stateEdgeGroup) {
    chart.stateEdgeGroup.destroy();
    chart.stateEdgeClip.destroy();
  }
  chart.stateEdgeClip = chart.renderer.clipRect(0, 0, chart.plotWidth, chart.plotHeight);
  chart.stateEdgeGroup = chart.renderer.g('state-edges')
    .attr({ zIndex: 4, translateX: chart.plotLeft, translateY: chart.plotTop })
    .clip(chart.stateEdgeClip)
    .add();
  chart.series.filter(series => series.visible && series.graph && series.graph.attr('d')).forEach(series => {
    const path = series.graph.attr('d');
    chart.renderer.path(path).attr({ stroke: '#FFFFFF', 'stroke-width': 3, fill: 'none' }).add(chart.stateEdgeGroup);
    chart.renderer.path(path).attr({ stroke: edgeColor(series), 'stroke-width': 1, fill: 'none' }).add(chart.stateEdgeGroup);
  });
}

function keyDotStyle(color) {
  return color ? { background: stateFill(color), boxShadow: `inset 0 0 0 1px ${stateEdge(color)}` } : {};
}

function withUnit(value, unit) {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

const BAR_RADIUS = 2;
const BAR_STATES = {
  hover: { brightness: 0 },
  inactive: { opacity: 0.35 }
};
const BAR_FIVE_STEP_MAX = 25;

// Highcharts chooses tick intervals above 25
function barTickInterval(max) {
  return max <= BAR_FIVE_STEP_MAX ? 5 : null;
}

// Highcharts 9 internals (per-element heightSetter and afterSetters, not public API)
function clipBottom(graphic, renderer) {
  const clip = renderer.clipRect(0, 0, 9999, 0);
  const { afterSetters, destroy } = graphic;
  Object.assign(graphic, {
    bottomClip: clip,
    height: graphic.attr('height'),
    heightSetter(value) {
      this.height = value;
    },
    afterSetters() {
      afterSetters.call(this);
      this.element.setAttribute('height', this.height + this.attr('r'));
      clip.attr({ height: this.attr('y') + this.height });
    },
    destroy() {
      clip.destroy();
      return destroy.call(this);
    }
  });
}

function roundTops(chart, radius = BAR_RADIUS) {
  const points = _(chart.series)
    .filter(series => series.visible && (series.type === 'column' || series.type === 'bar'))
    .flatMap('points')
    .filter(point => point.graphic && point.shapeArgs)
    .value();
  const tops = new Set(_(points)
    .filter(point => point.y > 0)
    .groupBy('x')
    .map(stack => _.minBy(stack, 'shapeArgs.y'))
    .value());
  points.forEach(point => {
    const { graphic } = point;
    const r = tops.has(point) ? Math.min(radius, point.shapeArgs.width / 2) : 0;
    if (r && !graphic.bottomClip) {
      clipBottom(graphic, chart.renderer);
    }
    graphic.attr({ r });
    if (graphic.bottomClip) {
      // SVG rects round all corners; clip the lower two
      graphic.clip(r ? graphic.bottomClip : undefined);
    }
  });
}

function tvTooltipLead(value, context) {
  return `<p class="tooltip-value">${value}</p><p class="tooltip-context">${context}</p>`;
}

function segmentLabelStyle(fontSize, fontWeight = '600') {
  return {
    align: 'center',
    verticalAlign: 'middle',
    inside: true,
    backgroundColor: 'none',
    borderWidth: 0,
    padding: 0,
    shadow: false,
    style: {
      color: 'contrast',
      fontSize,
      fontWeight,
      textOutline: 'none'
    }
  };
}

// labels render before the later series' points exist
function columnShare(series, x) {
  const stacked = series.chart.series.filter(item => item.visible && item.options.stacking && item.xData.includes(x));
  return wholeShares(stacked.map(item => item.yData[item.xData.indexOf(x)]))[stacked.indexOf(series)];
}

function segmentLabel(context) {
  const { point, series } = context;
  const shape = point && point.shapeArgs;
  const chart = series && series.chart;
  if (!chart || !point) {
    return null;
  }
  const share = columnShare(series, point.x);
  // a hidden or non-stacked series still draws hidden labels
  if (!share) {
    return null;
  }
  const text = escapeHTML(share);
  // without shape or renderer (e.g., export path), return the share without fit checking
  if (!shape || !chart.renderer) {
    return text;
  }
  const { fontSize, fontWeight } = series.options.dataLabels.style;
  const box = textBox(chart, text, fontSize, fontWeight);
  // a bar chart is a column chart turned on its side, so its shapes keep the column's sides: a bar's length is its height
  const [length, thickness] = chart.inverted ? [shape.height, shape.width] : [shape.width, shape.height];
  if (box.width > length || box.height > thickness) {
    return null;
  }
  const color = segmentTextColor(point.color);
  // main.scss sets font-weight on every element, so the span's tspan does not inherit the label's weight
  return color ? `<span style="color: ${color}; font-weight: ${fontWeight}">${text}</span>` : text;
}

const CARD_LINE_COLORS = ['#7CB5EC', '#3C78B4', '#A8CBEA', '#5A96D0', '#2B5C8C', '#C6DDF1'];

const TREND_LINE_COLOR = CARD_LINE_COLORS[1];
// a card's lines start with the trend blue; the pale card blues are deepened in their own hue to reach 3:1 on white:
// #7CB5EC (2.17:1) to #4B99E5 (3.01:1), #A8CBEA (1.69:1) to #235C8F (7:1, so it stays apart from the first two),
// #C6DDF1 (1.40:1) to #569AD5 (3.01:1)
const CARD_TREND_COLORS = [TREND_LINE_COLOR, '#4B99E5', '#235C8F', CARD_LINE_COLORS[3], CARD_LINE_COLORS[4], '#569AD5'];
const TREND_LINE_WIDTH = 2.5;
const TREND_AREA_RGB = '213, 231, 242';
const TREND_AREA_TOP_OPACITY = 0.45;
const TREND_GRID_COLOR = '#E6F1F7';
const TREND_AXIS_LINE_COLOR = '#D5E7F2';
const TREND_LABEL_COLOR = '#657387';
const TREND_Y_TICKS = 4;
const TREND_MARKER_RADIUS = 4;
const TREND_PERCENT_TICKS = [0, 50, 100];

function trendLineChart(unit) {
  const labelStyle = { color: TREND_LABEL_COLOR, fontSize: '12px' };
  return {
    xAxis: {
      gridLineWidth: 1,
      gridLineColor: TREND_GRID_COLOR,
      lineColor: TREND_AXIS_LINE_COLOR,
      tickColor: TREND_AXIS_LINE_COLOR,
      labels: { style: labelStyle }
    },
    yAxis: {
      min: 0,
      ...(unit === '%' ? { tickPositions: TREND_PERCENT_TICKS } : { tickAmount: TREND_Y_TICKS }),
      allowDecimals: false,
      gridLineWidth: 1,
      gridLineColor: TREND_GRID_COLOR,
      labels: {
        enabled: true,
        style: labelStyle,
        formatter() {
          return unit === '%' ? `${this.value}%` : withUnit(this.value, unit);
        }
      }
    }
  };
}

function trendPoints(series) {
  const marker = {
    enabled: false,
    symbol: 'circle',
    radius: TREND_MARKER_RADIUS,
    lineWidth: 1.5,
    lineColor: '#FFFFFF',
    states: { hover: { enabled: true } }
  };
  const data = _.isEmpty(series.data)
    ? series.data
    : [..._.initial(series.data), _.merge({}, _.last(series.data), { marker: { enabled: true } })];
  return {
    ...series,
    data,
    marker,
    states: { hover: { lineWidthPlus: 0, halo: { size: 0 } } }
  };
}

function trendLineSeries(series, filled) {
  return {
    ...trendPoints(series),
    lineWidth: TREND_LINE_WIDTH,
    clip: false,
    ...(filled ? {
      type: 'areaspline',
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [[0, `rgba(${TREND_AREA_RGB}, ${TREND_AREA_TOP_OPACITY})`], [1, `rgba(${TREND_AREA_RGB}, 0)`]]
      }
    } : {})
  };
}

function cardLegend(isFullScreen) {
  return {
    maxHeight: 47,
    symbolWidth: isFullScreen ? 10 : 8,
    symbolHeight: isFullScreen ? 10 : 8,
    symbolRadius: isFullScreen ? 5 : 4,
    itemMarginBottom: isFullScreen ? 5 : 0,
    itemStyle: {
      fontSize: isFullScreen ? '12px' : '11px',
      fontWeight: 'normal'
    },
    navigation: {
      enabled: true,
      activeColor: '#004080',
      inactiveColor: '#D5E7F2',
      arrowSize: 8,
      style: {
        color: '#545D66',
        fontSize: '11px'
      }
    }
  };
}

// legend. Plain text, because an image can't hold the screen's HTML labels
function pieExportLabels(valueText) {
  return {
    enabled: true,
    useHTML: false,
    connectorWidth: 2,
    distance: 20,
    crop: false,
    overflow: 'justify',
    style: {
      color: '#001A33',
      fontSize: '16px',
      fontWeight: 'normal',
      textOutline: 'none'
    },
    formatter() {
      const name = escapeHTML(this.key || this.point.name);
      return `<span style="font-weight: 600">${name} (${this.percentage.toFixed(2)}%)</span><br/>${escapeHTML(valueText(this.point))}`;
    }
  };
}

function pieBesideNames(chart, wallmode) {
  const width = chart.chartWidth;
  const lastName = wallmode && chart.renderTo.parentNode.querySelector('.pie-legend > :last-child');
  const names = lastName ? PIE_NAMES_LEFT + lastName.offsetLeft + lastName.offsetWidth : Math.min(width * 0.48, 300);
  const radius = (Math.min(chart.plotWidth || width, chart.plotHeight || 0) * 0.9) / 2;
  return { x: Math.min(width * 0.74, names + 32 + radius), radius };
}

function cardPieGroupOffset(chart, wallmode) {
  const width = (chart && chart.chartWidth) || 0;
  if (!wallmode || !width) {
    return 0;
  }
  const { x, radius } = pieBesideNames(chart, wallmode);
  const group = x + radius - PIE_NAMES_LEFT;
  return group * 2 < width ? Math.floor((width - x - radius - PIE_NAMES_LEFT) / 2) : 0;
}

function cardPieCenter(chart, wallmode) {
  const width = (chart && chart.chartWidth) || 0;
  if (!width) {
    return ['74%', '50%'];
  }
  const x = pieBesideNames(chart, wallmode).x + cardPieGroupOffset(chart, wallmode);
  return [`${Math.round((x / width) * 100)}%`, '50%'];
}

function placePieNames(component, shown) {
  const offset = shown ? cardPieGroupOffset(component.chart, component.props.wallmode) : 0;
  if (offset !== (component.state.pieNamesOffset || 0)) {
    component.setState({ pieNamesOffset: offset });
  }
}

function pieLegend(isFullScreen) {
  const sizes = cardLegend(isFullScreen);
  return isFullScreen
    ? { ...sizes, maxHeight: null, layout: 'horizontal', align: 'center', verticalAlign: 'bottom', width: null, alignColumns: false, itemMarginBottom: 9 }
    : { ...sizes, maxHeight: null, layout: 'vertical', align: 'left', verticalAlign: 'top', width: 230 };
}

function pieLabelStyle(context, distance) {
  const { chart, center } = context.series || {};
  if (!chart) {
    return 'color: #001A33';
  }
  const width = Math.max(80, Math.floor((chart.plotWidth - ((center && center[2]) || 0)) / 2 - distance - 16));
  return `color: #001A33; max-width: ${width}px`;
}

const SLICE_EDGE_INSET = 1.5;
const SLICE_LIFT = 4;

const DONUT_OPTIONS = {
  innerSize: '68%',
  startAngle: 0,
  borderWidth: 2,
  borderColor: '#FFFFFF',
  states: {
    hover: {
      brightness: 0,
      halo: {
        size: 0
      }
    },
    inactive: {
      opacity: 0.6
    }
  }
};

function donutData(data) {
  return _.map(stateSeries(data, 'edgeColor'), (slice, i) => ({ ...slice, stateColor: data[i].color }));
}

function drawSliceEdges(chart) {
  chart.series.forEach(series => series.points.forEach(point => {
    if (point.edge) {
      point.edge = point.edge.destroy();
    }
    const args = point.shapeArgs;
    if (!point.visible || !point.graphic || !args || !point.options.edgeColor) {
      return;
    }
    const outer = args.r - SLICE_EDGE_INSET;
    const inner = args.innerR + SLICE_EDGE_INSET;
    const start = args.start + SLICE_EDGE_INSET / outer;
    const end = args.end - SLICE_EDGE_INSET / outer;
    if (end <= start || outer <= inner) {
      return;
    }
    point.edge = chart.renderer.arc(args.x, args.y, outer, inner, start, end)
      .attr({
        fill: 'none',
        stroke: point.options.edgeColor,
        'stroke-width': 1,
        zIndex: 1,
        translateX: point.graphic.translateX || 0,
        translateY: point.graphic.translateY || 0
      })
      .css({ pointerEvents: 'none' })
      .add(series.group);
  }));
}

function liftSlice(point, on) {
  const angle = point.angle || 0;
  const lift = on ? { translateX: Math.cos(angle) * SLICE_LIFT, translateY: Math.sin(angle) * SLICE_LIFT } : { translateX: 0, translateY: 0 };
  [point.graphic, point.edge].forEach(element => element && element.animate(lift));
}

const COLUMNS = [45, 70, 55, 85, 60, 75, 40, 65];
const DOTS = [
  [6, 22], [10, 48], [15, 30], [21, 64], [26, 18], [31, 40], [37, 26], [42, 72], [47, 36], [52, 54], [57, 20], [62, 44],
  [67, 30], [72, 66], [77, 24], [82, 50], [87, 34], [92, 58], [95, 28]
];
const ROWS = [92, 76, 84, 68, 80];
const LINES = [
  'M0,30 C12,24 22,32 34,26 S58,14 72,18 S90,10 100,8',
  'M0,36 C14,34 26,38 40,33 S62,26 76,29 S92,22 100,20'
];
const BANDS = [
  'M0,40 L0,30 C20,27 40,22 60,16 S90,6 100,4 L100,40 Z',
  'M0,40 L0,35 C22,33 42,29 62,25 S90,17 100,15 L100,40 Z',
  'M0,40 L0,38 C24,37 46,35 66,32 S90,27 100,26 L100,40 Z'
];

const SHAPES = ['columns', 'line', 'scatter', 'area', 'pie', 'table'];

function shapeForChartType(type) {
  if (type === 'pie') return 'pie';
  if (type === 'scatter' || type === 'bubble') return 'scatter';
  if (type === 'area' || type === 'areaspline') return 'area';
  if (type === 'line' || type === 'spline') return 'line';
  return 'columns';
}

function shapeMarkup(shape) {
  switch (shape) {
    case 'line':
      return `<svg viewBox="0 0 100 40" preserveAspectRatio="none">${LINES.map(d => `<path d="${d}" />`).join('')}</svg>`;
    case 'area':
      return `<svg viewBox="0 0 100 40" preserveAspectRatio="none">${BANDS.map(d => `<path d="${d}" />`).join('')}</svg>`;
    case 'scatter':
      return DOTS.map(([left, bottom]) => `<i style="left: ${left}%; bottom: ${bottom}%"></i>`).join('');
    case 'pie':
      return '<b></b>';
    case 'table':
      return ROWS.map(width => `<em style="width: ${width}%"></em>`).join('');
    default:
      return COLUMNS.map(height => `<span style="height: ${height}%"></span>`).join('');
  }
}

function placeholderMarkup(shape, loading) {
  const known = SHAPES.indexOf(shape) === -1 ? 'columns' : shape;
  return shapeMarkup(known) + (loading ? '' : '<p class="chart-placeholder-text">No data available</p>');
}

function statsLoading(isLoading, data, stats) {
  return !!isLoading || (_.size(data) > 0 && !Array.isArray(stats));
}

function hasValues(series) {
  return _.some(_.flatMap(series, 'data'), point => !!point && point.y > 0);
}

function hasWorkItems(days) {
  return _.some(days, day => _.some(day && day.series, series => !!series && !!series.data && series.data.length > 0));
}

function hasRates(groups) {
  return _.some(groups, group => _.some(group && group.data, point => !!point &&
    _.some([point.totalArrival, point.totalThroughput, point.arrival, point.throughput], _.isFinite)));
}

function rankedShares(items) {
  const ranked = [...items].sort((a, b) => (b.y || 0) - (a.y || 0));
  const shares = wholeShares(ranked.map(item => item.y || 0));
  return ranked.map((item, index) => ({ ...item, share: shares[index] }));
}

const REFERENCE_LINE_COLOR = '#93a1a8';
const STAT_LINE_COLORS = { Mean: REFERENCE_LINE_COLOR, Median: PERCENTILE_LINE_COLORS[50], Mode: CARD_TREND_COLORS[2] };

function referenceLine(text, value, style) {
  return {
    color: REFERENCE_LINE_COLOR,
    dashStyle: 'Dash',
    zIndex: 2,
    width: 1,
    value,
    label: {
      verticalAlign: 'top',
      align: 'left',
      textAlign: 'left',
      rotation: 0,
      x: 4,
      y: 12,
      text,
      className: 'chart-line-label',
      // the whole name on one line: Highcharts would cut a name near the chart's right edge short with "…"
      style: {
        ...style,
        whiteSpace: 'nowrap',
        textOverflow: 'none'
      }
    }
  };
}

function percentileLine(name, value, style) {
  const line = referenceLine(`${name}%`, value, { ...style, color: PERCENTILE_LABEL_COLORS[name] });
  return { ...line, color: PERCENTILE_LINE_COLORS[name], percentile: true };
}

function hoverLine(text, value) {
  return {
    name: 'tooltip',
    color: 'transparent',
    width: 4,
    zIndex: 2,
    value,
    text
  };
}

function groupReferenceLines(lines, ticks, unit, plotlinesTooltip, formatValue = value => value) {
  const byValue = items => _(items)
    .groupBy('value')
    .toPairs()
    .orderBy(pair => +pair[0])
    .value();

  const grouped = [];
  _.forEachRight(byValue(lines), ([value, same]) => {
    const line = same.find(item => item.percentile) || same[0];
    const shown = (same.find(item => item.shownValue !== undefined) || {}).shownValue;
    line.label.text = `${same.map(item => item.label.text).join(', ')} · ${formatValue(shown !== undefined ? shown : value)}${unit}`;
    grouped.push(line);
  });

  const hovers = [];
  _.forEachRight(byValue(ticks), ([, same]) => {
    const tick = same[0];
    tick.events = plotlinesTooltip(same.map(item => item.text).filter(Boolean).join(', '));
    hovers.push(tick);
  });

  return grouped.concat(hovers);
}

function placeTopLineLabels(chart, topSpace) {
  const axis = chart.xAxis && chart.xAxis[0];
  if (!axis || !axis.plotLinesAndBands) {
    return;
  }
  const rows = [];
  let rowHeight = 16;
  const placed = axis.plotLinesAndBands
    .filter(item => item.label && item.options.label && item.options.label.className === 'chart-line-label')
    .map(item => ({ label: item.label, x: axis.toPixels(+item.options.value) }))
    .sort((a, b) => a.x - b.x)
    .map(({ label, x }) => {
      const box = label.getBBox();
      rowHeight = Math.max(rowHeight, Math.ceil(box.height) + 2);
      // 8px clear of the plot's left edge, so a label beside the y axis's top value doesn't read on from it ("12 Mode")
      const left = Math.min(Math.max(x - box.width / 2, chart.plotLeft + 8), chart.chartWidth - box.width);
      let row = 0;
      while ((rows[row] || []).some(([start, end]) => left < end + 8 && left + box.width + 8 > start)) {
        row += 1;
      }
      rows[row] = (rows[row] || []).concat([[left, left + box.width]]);
      return { label, left, row };
    });
  const marginTop = topSpace + rows.length * rowHeight;
  if (chart.options.chart.marginTop !== marginTop) {
    chart.update({ chart: { marginTop } });
    return;
  }
  placed.forEach(({ label, left, row }) => {
    label.attr({ x: left, y: chart.plotTop - 5 - row * rowHeight });
  });
}

function fitRightLineLabels(chart, lines, style, minMargin) {
  if (!chart) {
    return;
  }
  const widths = lines.map(line => {
    const text = chart.renderer.text(line.label.text, -9999, -9999).css(style).add();
    const width = text.getBBox().width;
    text.destroy();
    return width;
  });
  const margin = Math.max(minMargin, Math.ceil(Math.max(0, ...widths)) + 16);
  lines.forEach(line => {
    line.label.x = margin - 4;
  });
  if (chart.options.chart.marginRight !== margin) {
    chart.update({ chart: { marginRight: margin } }, false);
  }
}

function dateRange(start, end, dateFormat) {
  const withoutYear = dateFormat.replace(/[\s,./-]*Y+[\s,./-]*/g, ' ').trim();
  return `${start.format(start.isSame(end, 'year') ? withoutYear : dateFormat)} - ${end.format(dateFormat)}`;
}

function periodTitle(date, timeUnit, dateFormat) {
  const range = (start, end) => dateRange(start, end, dateFormat);
  if (timeUnit === 'week') {
    return range(moment(date).startOf('isoWeek'), moment(date).endOf('isoWeek'));
  }
  if (timeUnit === '2weeks') {
    return range(moment(date), moment(date).add(13, 'days'));
  }
  if (timeUnit === 'month') {
    return moment(date).format('MMMM YYYY');
  }
  return moment(date).format(dateFormat);
}

function selectedMarker(radius) {
  return {
    fillColor: null,
    lineColor: '#FFFFFF',
    lineWidth: 2,
    radius
  };
}

function hoverMarker() {
  return {
    lineColor: '#FFFFFF',
    lineWidth: 2,
    radiusPlus: 1
  };
}

function selectOnly(chart, point) {
  if (!chart || !chart.getSelectedPoints || !point.select) {
    return;
  }
  chart.getSelectedPoints().forEach(item => item.select(false, true));
  point.select(true, true);
}

function drawSelectionHalo(chart, previous) {
  if (previous) {
    previous.destroy();
  }
  const point = chart && chart.getSelectedPoints && chart.getSelectedPoints()[0];
  if (!point || !point.graphic || !point.series.visible) {
    return null;
  }
  return chart.renderer
    .path(point.haloPath(14))
    .attr({ fill: point.color, opacity: 0.25, zIndex: -1, 'class': 'highcharts-halo' })
    .add(point.series.markerGroup || point.series.group);
}

const ZOOM_MASK_COLOR = 'rgba(0, 64, 128, 0.06)';
const ZOOM_MASK_BORDER = '#D5E7F2';

const RESET_ZOOM_THEME = {
  zIndex: 20,
  fill: '#FFFFFF',
  stroke: '#D5E7F2',
  'stroke-width': 1,
  r: 8,
  padding: 6,
  style: {
    color: '#001A33',
    fontSize: '12px',
    fontWeight: '500'
  }
};

const RESET_ZOOM_BESIDE_BUTTONS = {
  relativeTo: 'chart',
  position: { align: 'right', verticalAlign: 'top', x: -68, y: 0 }
};

const RESET_ZOOM_HOVER = {
  fill: '#EFF6FB'
};

function daysInUnit(days, timeUnit) {
  return Math.ceil(moment.duration(days, 'day').as(timeUnit));
}

export default {
  shareLabel,
  wholeShares,
  SEGMENT_LABEL_SAMPLE,
  labelFits,
  segmentLabelStyle,
  segmentLabel,
  tvTooltipLead,
  withUnit,
  roundTops,
  BAR_STATES,
  barTickInterval,
  withOpacity,
  stateFill,
  stateEdge,
  paleMarkEdge,
  stateSeries,
  drawStateEdges,
  keyDotStyle,
  CARD_LINE_COLORS,
  TREND_LINE_COLOR,
  CARD_TREND_COLORS,
  TREND_GRID_COLOR,
  TREND_AXIS_LINE_COLOR,
  TREND_LABEL_COLOR,
  trendLineChart,
  trendLineSeries,
  cardLegend,
  pieExportLabels,
  cardPieCenter,
  placePieNames,
  pieLegend,
  pieLabelStyle,
  DONUT_OPTIONS,
  donutData,
  drawSliceEdges,
  liftSlice,
  shapeForChartType,
  placeholderMarkup,
  hasValues,
  hasWorkItems,
  hasRates,
  statsLoading,
  rankedShares,
  referenceLine,
  percentileLine,
  STAT_LINE_COLORS,
  hoverLine,
  groupReferenceLines,
  placeTopLineLabels,
  fitRightLineLabels,
  dateRange,
  periodTitle,
  selectedMarker,
  hoverMarker,
  selectOnly,
  drawSelectionHalo,
  ZOOM_MASK_COLOR,
  ZOOM_MASK_BORDER,
  RESET_ZOOM_THEME,
  RESET_ZOOM_BESIDE_BUTTONS,
  RESET_ZOOM_HOVER,
  daysInUnit
};
